document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const q = (selector, scope = document) => scope.querySelector(selector);
const qa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

class FortyRoundsTheme {
  constructor() {
    this.bindMenu();
    this.bindFloatingSupport();
    this.bindMirrorReveal();
    this.bindRoundProtocol();
    this.bindSignalDemo();
    this.bindProductForms();
    this.bindQuantity();
    this.bindCursorGlow();
    this.bindBackToTop();
    this.bindDisclosure();
  }

  bindMenu() {
    const trigger = q('[data-menu-toggle]');
    const drawer = q('[data-mobile-menu]');
    if (!trigger || !drawer || trigger.dataset.frMenuBound === 'true') return;
    trigger.dataset.frMenuBound = 'true';

    const panel = q('[role="dialog"]', drawer);
    const lifecycle = new AbortController();
    const events = { signal: lifecycle.signal };
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
    let returnFocus = trigger;
    let background = [];
    const visible = (element) => element && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
    const focusable = () => qa(selector, drawer).filter((element) => element.tabIndex >= 0 && visible(element));
    const notify = () => document.dispatchEvent(new CustomEvent('fr:menu-state'));
    const isolateBackground = () => {
      let branch = drawer;
      while (branch.parentElement && branch !== document.body) {
        [...branch.parentElement.children].forEach((element) => {
          if (element === branch || ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(element.tagName)) return;
          background.push({ element, inert: element.inert });
          element.inert = true;
        });
        branch = branch.parentElement;
      }
    };
    const close = (restoreFocus = true) => {
      if (drawer.hidden && !background.length) return;
      drawer.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
      background.forEach(({ element, inert }) => { element.inert = inert; });
      background = [];
      notify();
      if (restoreFocus) {
        const target = visible(returnFocus) ? returnFocus : trigger;
        if (visible(target)) target.focus({ preventScroll: true });
      }
    };

    trigger.addEventListener('click', () => {
      if (!drawer.hidden) {
        close();
        return;
      }
      returnFocus = document.activeElement;
      drawer.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('menu-open');
      isolateBackground();
      notify();
      const first = q('[data-menu-close]', drawer) || focusable()[0] || panel;
      if (first) first.focus({ preventScroll: true });
    }, events);

    qa('[data-menu-close]', drawer).forEach((button) => button.addEventListener('click', () => close(), events));
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) close();
      const link = event.target.closest('a[href]');
      if (link && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) close();
    }, events);
    document.addEventListener('focusin', (event) => {
      if (drawer.hidden || drawer.contains(event.target)) return;
      const first = focusable()[0] || panel;
      if (first) first.focus({ preventScroll: true });
    }, events);
    document.addEventListener('keydown', (event) => {
      if (drawer.hidden || event.defaultPrevented) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) {
        event.preventDefault();
        if (panel) panel.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !drawer.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !drawer.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }, events);
    window.addEventListener('pageshow', () => close(false), events);
    window.addEventListener('pagehide', () => close(false), events);
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(drawer)) return;
      close(false);
      lifecycle.abort();
      delete trigger.dataset.frMenuBound;
    }, events);
  }

  bindFloatingSupport() {}

  bindMirrorReveal() {
    qa('[data-mirror-reveal]').forEach((section) => {
      if (section.dataset.frMirrorBound === 'true') return;
      section.dataset.frMirrorBound = 'true';
      const display = q('[data-mirror-message]', section);
      const status = q('[data-mirror-status]', section);
      qa('[data-mirror-mode]', section).forEach((button) => {
        button.addEventListener('click', () => {
          const mirrored = button.dataset.mirrorMode === 'mirror';
          display.classList.toggle('is-readable', mirrored);
          qa('[data-mirror-mode]', section).forEach((item) => item.classList.toggle('is-active', item === button));
          qa('[data-mirror-mode]', section).forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
          if (status) status.textContent = mirrored ? 'Mirror view: message revealed' : 'Garment view: message reversed';
        });
      });
    });
  }

  bindRoundProtocol() {
    const messages = {
      train: ['TRAIN / TODAY’S STANDARD', 'FORM OVER EGO. FINISH THE REP YOU CAN OWN.'],
      work: ['WORK / TODAY’S STANDARD', 'DO THE HARD TASK FIRST. LET COMPLETION BUILD MOMENTUM.'],
      recover: ['RECOVER / TODAY’S STANDARD', 'RECOVERY IS NOT RETREAT. PREPARE FOR WHAT COMES NEXT.'],
      lead: ['LEAD / TODAY’S STANDARD', 'SET THE PACE. MAKE THE PEOPLE BESIDE YOU BETTER.']
    };

    qa('[data-round-protocol]').forEach((section) => {
      if (section.dataset.frProtocolBound === 'true') return;
      section.dataset.frProtocolBound = 'true';
      const label = q('[data-round-label]', section);
      const message = q('[data-round-message]', section);
      const seconds = q('[data-round-seconds]', section);
      const progress = q('[data-round-progress]', section);
      const state = q('[data-round-state]', section);
      const live = q('[data-round-live]', section);
      const start = q('[data-round-start]', section);
      let selectedMode = 'train';
      let timerId;

      const setMode = (mode) => {
        selectedMode = mode;
        label.textContent = messages[mode][0];
        message.textContent = messages[mode][1];
        qa('[data-round-mode]', section).forEach((button) => {
          const active = button.dataset.roundMode === mode;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      };

      qa('[data-round-mode]', section).forEach((button) => {
        button.addEventListener('click', () => {
          if (timerId) return;
          setMode(button.dataset.roundMode);
        });
      });

      start.addEventListener('click', () => {
        if (timerId) return;
        let remaining = 40;
        setMode(selectedMode);
        seconds.textContent = '40';
        state.textContent = 'IN PROGRESS';
        start.disabled = true;
        start.textContent = 'ROUND IN PROGRESS';
        live.textContent = `${messages[selectedMode][0]}. 40-second focus reset started.`;
        progress.style.setProperty('--round-progress', '1');

        timerId = window.setInterval(() => {
          remaining -= 1;
          seconds.textContent = String(remaining);
          progress.style.setProperty('--round-progress', String(remaining / 40));

          if (remaining === 0) {
            window.clearInterval(timerId);
            timerId = undefined;
            state.textContent = 'ROUND COMPLETE';
            message.textContent = 'ROUND COMPLETE. TAKE THE NEXT ONE.';
            start.disabled = false;
            start.textContent = 'RUN IT AGAIN';
            live.textContent = 'Round complete. Take the next one.';
          }
        }, 1000);
      });
    });
  }

  bindSignalDemo() {
    qa('[data-signal-demo]').forEach((section) => {
      if (section.dataset.frSignalBound === 'true') return;
      section.dataset.frSignalBound = 'true';
      const button = q('[data-signal-toggle]', section);
      const status = q('[data-signal-status]', section);
      if (!button) return;

      button.addEventListener('click', () => {
        const active = !section.classList.contains('is-signal-active');
        section.classList.toggle('is-signal-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.textContent = active ? 'LIGHT RETURN / ACTIVE' : (button.dataset.offLabel || 'TEST LIGHT RETURN');
        if (status) status.textContent = active
          ? 'Visual demo: simulated headlights are striking the reflective zones.'
          : 'Visual demo: headlight simulation off.';
      });
    });
  }

  bindProductForms() {
    qa('[data-product-root]').forEach((root) => {
      if (root.dataset.frProductBound === 'true') return;
      root.dataset.frProductBound = 'true';
      const select = q('[data-variant-select]', root);
      const optionSelectors = qa('[data-product-option]', root);
      const colorSelector = optionSelectors.find(field => field.dataset.optionKind === 'color');
      const sizeSelector = optionSelectors.find(field => field.dataset.optionKind === 'size');
      const price = q('[data-product-price]', root);
      const compare = q('[data-product-compare]', root);
      const button = q('[data-add-to-cart]', root);
      const dynamic = q('.fr-product__dynamic', root);
      const message = q('[data-selection-message]', root);
      const media = qa('[data-media-id]', root);
      const thumbs = qa('[data-gallery-thumb]', root);
      const apparelGallery = q('[data-apparel-gallery]', root);
      const showMedia = (id) => {
        if (apparelGallery) {
          root.dispatchEvent(new CustomEvent('fr:variant-media', { detail: { mediaId: id } }));
          return;
        }
        const target = media.find(item => item.dataset.mediaId === String(id)) || media[0];
        if (!target) return;
        media.forEach(item => item.classList.toggle('is-active', item === target));
        thumbs.forEach(thumb => thumb.setAttribute('aria-pressed', String(thumb.dataset.galleryThumb === target.dataset.mediaId)));
      };
      thumbs.forEach(thumb => thumb.addEventListener('click', () => showMedia(thumb.dataset.galleryThumb)));
      showMedia(select?.selectedOptions[0]?.dataset.featuredMedia);
      const openSize = () => {
        if (location.hash === '#SizeGuide') {
          const detail = q('[data-size-details]', root);
          if (detail) detail.open = true;
        }
      };
      qa('a[href="#SizeGuide"]', root).forEach(link => link.addEventListener('click', () => {
        const detail = q('[data-size-details]', root);
        if (detail) detail.open = true;
      }));
      window.addEventListener('hashchange', openSize);
      openSize();
      if (!select) return;
      const variants = [...select.options];
      const initialVariant = select.selectedOptions[0];
      const matchesSelection = variant => optionSelectors.every(field => !field.value || variant.dataset['option' + field.dataset.productOption] === field.value);
      const selectedVariant = () => optionSelectors.every(field => field.value)
        ? variants.find(matchesSelection)
        : undefined;
      const updatePrice = option => {
        // With no chosen size, show the real price range for the selected color.
        const candidates = option ? [option] : variants.filter(matchesSelection);
        if (!candidates.length) return;
        const sorted = [...candidates].sort((a, b) => Number(a.dataset.priceAmount) - Number(b.dataset.priceAmount));
        const low = sorted[0];
        const high = sorted[sorted.length - 1];
        if (price) price.textContent = low.dataset.priceAmount === high.dataset.priceAmount ? low.dataset.price : `${low.dataset.price}–${high.dataset.price}`;
        if (compare) {
          compare.textContent = option?.dataset.compare || '';
          compare.hidden = !option?.dataset.compare;
        }
      };
      const applyVariant = (option, updateUrl = true) => {
        const available = Boolean(option && option.dataset.available === 'true');
        select.disabled = !option;
        if (option) {
          select.value = option.value;
          showMedia(option.dataset.featuredMedia);
        } else {
          select.selectedIndex = -1;
          const colorMatch = variants.find(variant => variant.dataset.featuredMedia && (!colorSelector || variant.dataset['option' + colorSelector.dataset.productOption] === colorSelector.value));
          if (colorMatch) showMedia(colorMatch.dataset.featuredMedia);
        }
        updatePrice(option);
        if (updateUrl && history.replaceState) {
          const url = new URL(location.href);
          if (option) url.searchParams.set('variant', option.value);
          else url.searchParams.delete('variant');
          if (colorSelector?.value) url.searchParams.set('fr_color', colorSelector.value);
          if (sizeSelector?.value) url.searchParams.set('fr_size', sizeSelector.value);
          else url.searchParams.delete('fr_size');
          history.replaceState(history.state, '', url);
        }
        if (button) {
          button.disabled = !available;
          button.textContent = available ? button.dataset.addLabel : (option ? button.dataset.soldLabel : message?.dataset.chooseSize || button.dataset.soldLabel);
        }
        if (dynamic) dynamic.hidden = !available;
        const availability = q('[data-product-availability]', root);
        if (availability) {
          const colorAvailable = variants.some(variant => variant.dataset.available === 'true' && (!colorSelector?.value || variant.dataset['option' + colorSelector.dataset.productOption] === colorSelector.value));
          availability.textContent = (option ? available : colorAvailable) ? availability.dataset.available : availability.dataset.sold;
        }
        if (message) {
          message.hidden = available;
          message.textContent = option ? message.dataset.unavailable : message.dataset.chooseSize;
        }
      };
      const syncOptions = option => {
        if (option) optionSelectors.forEach(field => { field.value = option.dataset['option' + field.dataset.productOption] || ''; });
      };
      const syncChoices = () => {
        thumbs.forEach(thumb => { thumb.hidden = Boolean(thumb.dataset.galleryColor && thumb.dataset.galleryColor !== colorSelector?.value); });
        optionSelectors.forEach(field => {
          if (field === sizeSelector) [...field.options].forEach(option => {
            if (option.value) option.textContent = option.value + (option.disabled ? ' — ' + field.dataset.unavailableLabel : '');
          });
          const choices = qa('[data-option-choice="' + field.dataset.productOption + '"]', root);
          choices.forEach(choice => {
            const option = [...field.options].find(value => value.value === choice.value);
            choice.checked = choice.value === field.value;
            choice.disabled = !option || option.disabled;
            choice.closest('.fr-option-choice').hidden = false;
          });
          const label = q('[data-option-selected="' + field.dataset.productOption + '"]', root);
          if (label) label.textContent = field.value || '—';
          const group = q('[data-option-group="' + field.dataset.productOption + '"]', root);
          const note = group && q('[data-option-availability-note]', group);
          if (note) note.hidden = !choices.some(choice => choice.disabled);
        });
      };
      const validateDependentOptions = (clearUnavailable = false) => {
        optionSelectors.forEach(field => {
          // Every color remains explorable. Size availability is constrained to it.
          if (field === colorSelector) return;
          [...field.options].forEach(value => {
            if (!value.value) return;
            value.disabled = !variants.some(variant => variant.dataset.available === 'true'
              && variant.dataset['option' + field.dataset.productOption] === value.value
              && optionSelectors.every(other => other === field || !other.value || variant.dataset['option' + other.dataset.productOption] === other.value));
          });
          // A changed color may require a new size; never silently choose a size or color.
          if (clearUnavailable && field.selectedOptions[0]?.disabled) field.value = '';
        });
        syncChoices();
      };
      optionSelectors.forEach(field => field.addEventListener('change', () => {
        validateDependentOptions(true);
        applyVariant(selectedVariant());
      }));
      qa('[data-option-choice]', root).forEach(choice => choice.addEventListener('change', () => {
        if (!choice.checked || choice.disabled) return;
        const field = optionSelectors.find(item => item.dataset.productOption === choice.dataset.optionChoice);
        if (!field) return;
        field.value = choice.value;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        syncOptions(option);
        validateDependentOptions();
        applyVariant(option);
      });
      let initialized = false;
      const restore = () => {
        const params = new URL(location.href).searchParams;
        const id = params.get('variant');
        const deepLink = variants.find(item => item.value === id);
        if (deepLink) {
          // Preserve a valid deep link, including its visible sold-out combination.
          syncOptions(deepLink);
        } else if (!initialized) {
          syncOptions(initialVariant);
          if (sizeSelector) sizeSelector.value = [...sizeSelector.options].find(option => /^(m|medium)$/i.test(option.value))?.value || '';
          const savedColor = params.get('fr_color');
          if (colorSelector && [...colorSelector.options].some(option => option.value === savedColor)) colorSelector.value = savedColor;
          const savedSize = params.get('fr_size');
          if (sizeSelector && savedSize && [...sizeSelector.options].some(option => option.value === savedSize)) sizeSelector.value = savedSize;
          validateDependentOptions(true);
        }
        validateDependentOptions();
        applyVariant(deepLink || selectedVariant(), false);
        initialized = true;
      };
      root.querySelector('form[action*="/cart/add"]')?.addEventListener('submit', (event) => {
        const option = select.selectedOptions[0];
        if (!option || option.dataset.available !== 'true') { event.preventDefault(); applyVariant(option); }
      });
      window.addEventListener('popstate', restore);
      window.addEventListener('pageshow', restore);
      restore();
      const picker = q('[data-option-picker]', root);
      if (picker) { picker.hidden = false; root.classList.add('fr-picker-ready'); }
    });
  }
  bindQuantity() {
    qa('[data-quantity]').forEach((wrap) => {
      if (wrap.dataset.frQuantityBound === 'true') return;
      wrap.dataset.frQuantityBound = 'true';
      const input = q('input', wrap);
      qa('button', wrap).forEach((button) => {
        button.addEventListener('click', () => {
          const amount = button.dataset.quantityAction === 'increase' ? 1 : -1;
          input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + amount);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  bindCursorGlow() {
    const glow = q('.fr-cursor-glow');
    const root = document.documentElement;
    if (!glow || root.dataset.frCursorBound === 'true') return;
    root.dataset.frCursorBound = 'true';
    const pointer = matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const ring = document.createElement('span');
    const pulse = document.createElement('span');
    ring.className = 'fr-cursor-ring';
    pulse.className = 'fr-cursor-pulse';
    glow.append(ring, pulse);
    const targets = 'a[href], button, summary, [role="button"], input[type="submit"], input[type="button"], label[for]';
    let enabled = false;
    let seen = false;
    let frame = 0;
    let pulseAnimation;
    let x = -300;
    let y = -300;
    let targetX = -300;
    let targetY = -300;
    const hide = () => {
      seen = false;
      glow.classList.remove('is-visible', 'is-hover', 'is-down');
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      if (pulseAnimation) pulseAnimation.cancel();
    };
    const updatePreference = () => {
      enabled = pointer.matches && !reducedMotion.matches;
      root.classList.toggle('fr-cursor-enabled', enabled);
      if (!enabled) hide();
    };
    const paint = () => {
      frame = 0;
      if (!enabled || !seen) return;
      x += (targetX - x) * 0.28;
      y += (targetY - y) * 0.28;
      const remaining = Math.abs(targetX - x) + Math.abs(targetY - y);
      if (remaining < 0.3) { x = targetX; y = targetY; }
      glow.style.setProperty('--x', x + 'px');
      glow.style.setProperty('--y', y + 'px');
      if (remaining >= 0.3) frame = requestAnimationFrame(paint);
    };
    const isInteractive = (target) => {
      const element = target instanceof Element ? target.closest(targets) : null;
      return Boolean(element && !element.matches(':disabled, [aria-disabled="true"]'));
    };
    document.addEventListener('pointermove', (event) => {
      if (!enabled || event.pointerType === 'touch') return;
      targetX = event.clientX;
      targetY = event.clientY;
      if (!seen) { x = targetX; y = targetY; seen = true; }
      glow.classList.add('is-visible');
      glow.classList.toggle('is-hover', isInteractive(event.target));
      if (!frame) frame = requestAnimationFrame(paint);
    }, { passive: true });
    document.addEventListener('pointerover', (event) => {
      if (enabled) glow.classList.toggle('is-hover', isInteractive(event.target));
    }, { passive: true });
    document.addEventListener('pointerdown', (event) => {
      if (!enabled || event.pointerType === 'touch' || event.button !== 0) return;
      glow.classList.add('is-down');
      if (pulseAnimation) pulseAnimation.cancel();
      pulseAnimation = pulse.animate([
        { transform: 'translate(-50%, -50%) scale(0.35)', opacity: 0.75 },
        { transform: 'translate(-50%, -50%) scale(2.7)', opacity: 0 }
      ], { duration: 520, easing: 'cubic-bezier(.16,1,.3,1)' });
    }, { passive: true });
    document.addEventListener('pointerup', () => glow.classList.remove('is-down'), { passive: true });
    document.addEventListener('pointerout', (event) => { if (!event.relatedTarget) hide(); }, { passive: true });
    document.addEventListener('visibilitychange', () => { if (document.hidden) hide(); });
    window.addEventListener('blur', hide);
    pointer.addEventListener('change', updatePreference);
    reducedMotion.addEventListener('change', updatePreference);
    updatePreference();
  }

  bindBackToTop() {
    const control = q('[data-back-to-top]');
    if (!control || control.dataset.frBackToTopBound === 'true') return;
    control.dataset.frBackToTopBound = 'true';

    let frame;
    const update = () => {
      frame = undefined;
      control.classList.toggle('is-visible', (q('[data-page-scroll]')?.scrollTop || window.scrollY) > 640);
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    document.addEventListener('scroll', requestUpdate, { passive: true, capture: true });
    update();
  }

  bindDisclosure() {
    qa('.fr-nav-shop').forEach((detail) => {
      if (detail.dataset.frNavBound === 'true') return;
      detail.dataset.frNavBound = 'true';
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        qa('.fr-nav-shop', detail.parentElement).forEach((other) => {
          if (other !== detail) other.open = false;
        });
      });
      detail.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !detail.open) return;
        event.preventDefault();
        detail.open = false;
        detail.querySelector('summary')?.focus();
      });
      detail.querySelectorAll('a[href]').forEach((link) => link.addEventListener('click', () => { detail.open = false; }));
    });
    const discovery = q('.fr-desktop-discovery');
    if (discovery && discovery.dataset.frOutsideBound !== 'true') {
      discovery.dataset.frOutsideBound = 'true';
      document.addEventListener('pointerdown', (event) => {
        if (!discovery.contains(event.target)) qa('details[open]', discovery).forEach((detail) => { detail.open = false; });
      }, { passive: true });
    }
    qa('[data-disclosure-trigger]').forEach((trigger) => {
      if (trigger.dataset.frDisclosureBound === 'true') return;
      trigger.dataset.frDisclosureBound = 'true';
      trigger.addEventListener('click', () => {
        const target = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!target) return;
        const opening = target.hidden;
        target.hidden = !opening;
        trigger.setAttribute('aria-expanded', String(opening));
      });
    });
  }
}

const bootFortyRounds = () => new FortyRoundsTheme();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootFortyRounds);
} else {
  bootFortyRounds();
}

document.addEventListener('shopify:section:load', bootFortyRounds);

